import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { fitTextWithEllipsis } from './clampText';

/* Product description teaser: CSS-clamped (.pdp__description: 2 lines on
 * mobile, 3 on desktop) with the ellipsis rendered by US, not by WebKit — see
 * clampText.js for why. The element's text is written directly (no React
 * children) so the measuring probes never fight React's text node.
 * `onOverflowChange(true|false)` tells the page whether a "read more" link is
 * needed; it is only re-evaluated while collapsed. */
const fitsClamp = (el) => el.scrollHeight <= el.clientHeight + 1;

const ClampedText = ({ text, expanded, onOverflowChange, className }) => {
  const ref = useRef(null);
  const onOverflowRef = useRef(onOverflowChange);
  onOverflowRef.current = onOverflowChange;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const full = typeof text === 'string' ? text : '';

    const run = () => {
      if (expanded) { el.textContent = full; return; }
      el.textContent = full;
      // Hidden (display: none) → 0/0 → "fits"; the observer re-runs once visible.
      const overflow = !fitsClamp(el);
      onOverflowRef.current?.(overflow);
      if (!overflow) return;
      el.textContent = fitTextWithEllipsis(full, (candidate) => {
        el.textContent = candidate;
        return fitsClamp(el);
      });
    };

    run();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Width changes (orientation, fonts loading) change the line breaks.
    let lastWidth = el.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = el.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      run();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <span
      ref={ref}
      className={`pdp__description ${expanded ? 'pdp__description--expanded' : ''} ${className}`.trim()}
    />
  );
};

ClampedText.propTypes = {
  text: PropTypes.string,
  expanded: PropTypes.bool,
  /** Called with true when the collapsed text overflows the clamp (show "read more"). */
  onOverflowChange: PropTypes.func,
  className: PropTypes.string,
};
ClampedText.defaultProps = { text: '', expanded: false, onOverflowChange: undefined, className: '' };

export default ClampedText;
